import React, { useState, useEffect, useRef } from 'react';
import Slider from "react-slick";
import { Link } from 'react-router-dom';
import { 
  Shield, Search, Star, Users, User, CheckCircle, ArrowRight, Wrench, 
  Truck, PawPrint, Car, Landmark, CalendarDays, Sparkles, House, 
  Laptop, Droplets, Camera, UtensilsCrossed, GraduationCap, MapPin 
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Header from './header';
import CustomerHeader from '../customer/CustomerHeader';
import Footer from './Footer';
import Newsletter from './HomeNewsletter';
import RecentServices from './RecentServices';
import { useCustomerAuth } from '../../contexts/CustomerAuthContext';
import api, { API_BASE_URL } from '../../utils/api';



interface Suggestion {
  type: string;
  title: string;
  description: string;
  count: number;
  icon: string;
}

interface LocationSuggestion {
  type: string;
  name: string;
  full_name: string;
  worker_count: number;
  icon: string;
}

interface PopularData {
  popularCategories: Array<{
    id: number;
    name: string;
    icon: string;
    worker_count: number;
  }>;
  popularSkills: string[];
}

interface Review {
  id: number;
  rating: number;
  reviewText: string;
  reviewerName: string;
  reviewerPicture: string;
  workerName: string;
  serviceTitle: string;
  createdAt: string;
}

const getCategoryLucideIcon = (categoryName: string) => {
  const name = categoryName.toLowerCase();
  if (name.includes('moving') || name.includes('storage')) return <Truck className="h-6 w-6" />;
  if (name.includes('pet')) return <PawPrint className="h-6 w-6" />;
  if (name.includes('automotive') || name.includes('car')) return <Car className="h-6 w-6" />;
  if (name.includes('financial') || name.includes('bank') || name.includes('landmark')) return <Landmark className="h-6 w-6" />;
  if (name.includes('event') || name.includes('plan')) return <CalendarDays className="h-6 w-6" />;
  if (name.includes('beauty') || name.includes('wellness') || name.includes('spa')) return <Sparkles className="h-6 w-6" />;
  if (name.includes('home') || name.includes('repair') || name.includes('appliance')) return <House className="h-6 w-6" />;
  if (name.includes('technology') || name.includes('computer')) return <Laptop className="h-6 w-6" />;
  if (name.includes('cleaning')) return <Droplets className="h-6 w-6" />;
  if (name.includes('photography') || name.includes('photo')) return <Camera className="h-6 w-6" />;
  if (name.includes('catering') || name.includes('food')) return <UtensilsCrossed className="h-6 w-6" />;
  if (name.includes('tutoring') || name.includes('education')) return <GraduationCap className="h-6 w-6" />;
  return <Wrench className="h-6 w-6" />;
};

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useCustomerAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [locationQuery, setLocationQuery] = useState('');
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [popularData, setPopularData] = useState<PopularData | null>(null);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);

  // Location suggestions state
  const [locationSuggestions, setLocationSuggestions] = useState<LocationSuggestion[]>([]);
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);
  const [isLocationLoading, setIsLocationLoading] = useState(false);
  const [selectedLocationIndex, setSelectedLocationIndex] = useState(-1);

  const searchRef = useRef<HTMLDivElement>(null);
  const locationRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const locationDebounceRef = useRef<NodeJS.Timeout | null>(null);

  // Function to get category icon - handles different icon formats


  const IMAGE_BASE_URL = API_BASE_URL.replace('/api', '')
  const getCategoryIcon = (category: any) => {


    if (!category.icon) {
      return "/assets/images/cat-one.png";
    } else {

      return `${API_BASE_URL.replace(/\/api$/, "")}${category.icon}`;

    }
  };

  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    responsive: [
      {
        breakpoint: 1200,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
        }
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: false
        }
      }
    ]
  };

  var catslider = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 2.1,
    slidesToScroll: 1,
    centerMode: true,
    centerPadding: "5%",
  };


  // Fetch popular data and reviews on component mount
  useEffect(() => {
    fetchPopularData();
    fetchReviews();

    if (window.location.search) {
      searchRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
      if (locationRef.current && !locationRef.current.contains(event.target as Node)) {
        setShowLocationSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchPopularData = async () => {
    try {
      console.log(api, 'API_BASE_URL');

      const data = await api.get('/api/search/popular');
      if (data.success) {
        setPopularData(data.data);
      }
    } catch (error) {
      console.error('Error fetching popular data:', error);
      // Set fallback data if API fails
      setPopularData({
        popularCategories: [
          { id: 1, name: 'Appliance', icon: '/assets/images/cat-one.png', worker_count: 15 },
          { id: 2, name: 'Car Wash', icon: '/assets/images/cat-one.png', worker_count: 12 },
          { id: 3, name: 'Cleaning', icon: '/assets/images/cat-one.png', worker_count: 18 },
          { id: 4, name: 'Computer', icon: '/assets/images/cat-one.png', worker_count: 10 },
          { id: 5, name: 'Construction', icon: '/assets/images/cat-one.png', worker_count: 14 },
          { id: 6, name: 'Electrical', icon: '/assets/images/cat-one.png', worker_count: 16 }
        ],
        popularSkills: ['Appliance', 'Car Wash', 'Carpentry', 'Cleaning', 'Computer', 'Construction']
      });
    }
  };

  const fetchSuggestions = async (query: string) => {
    if (query.length < 2) {
      setSuggestions([]);
      return;
    }

    try {
      setIsLoading(true);
      // Fix the URL to include /api prefix
      const data = await api.get(`/api/search/suggestions?q=${encodeURIComponent(query)}`);

      if (data.success) {
        setSuggestions(data.suggestions);
      }
    } catch (error) {
      console.error('Error fetching suggestions:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchLocationSuggestions = async (query: string) => {
    if (query.length < 2) {
      setLocationSuggestions([]);
      return;
    }

    try {
      setIsLocationLoading(true);
      const data = await api.get(`/api/search/locations?q=${encodeURIComponent(query)}`);

      if (data.success) {
        setLocationSuggestions(data.suggestions);
      }
    } catch (error) {
      console.error('Error fetching location suggestions:', error);
    } finally {
      setIsLocationLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const data = await api.get('/api/services/reviews?limit=10');
      if (data.success) {
        console.log(data)
        setReviews(data.reviews);
        console.log('Reviews from API:', data.reviews);
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
      // Set fallback reviews if API fails
      setReviews([
        {
          id: 1,
          rating: 5,
          reviewText: "I had a great experience with ABC Electrical on the Services. The electrician arrived on time!!!",
          reviewerName: "Daniel Davis",
          reviewerPicture: "/assets/images/useer-image.png",
          workerName: "ABC Electrical",
          serviceTitle: "Electrical Service",
          createdAt: new Date().toISOString()
        },
        {
          id: 2,
          rating: 5,
          reviewText: "Outstanding service! Professional, timely, and high-quality work. Would definitely recommend!",
          reviewerName: "Sarah Johnson",
          reviewerPicture: "/assets/images/useer-image.png",
          workerName: "Home Solutions",
          serviceTitle: "Home Repair",
          createdAt: new Date().toISOString()
        },
        {
          id: 3,
          rating: 4,
          reviewText: "Great value for money. The team was skilled and completed the job efficiently.",
          reviewerName: "Michael Brown",
          reviewerPicture: "/assets/images/useer-image.png",
          workerName: "Quick Fix",
          serviceTitle: "Plumbing Service",
          createdAt: new Date().toISOString()
        }
      ]);
    }
  };

  const handleSearchInputChange = (value: string) => {
    setSearchQuery(value);
    setSelectedSuggestionIndex(-1); // Reset selection

    // Clear previous debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Debounce the search
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(value);
    }, 300);

    setShowSuggestions(true);
  };

  const handleLocationInputChange = (value: string) => {
    setLocationQuery(value);
    setSelectedLocationIndex(-1); // Reset selection

    // Clear previous debounce
    if (locationDebounceRef.current) {
      clearTimeout(locationDebounceRef.current);
    }

    // Debounce the search
    locationDebounceRef.current = setTimeout(() => {
      fetchLocationSuggestions(value);
    }, 300);

    setShowLocationSuggestions(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedSuggestionIndex(prev =>
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedSuggestionIndex(prev =>
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedSuggestionIndex >= 0 && selectedSuggestionIndex < suggestions.length) {
          handleSuggestionClick(suggestions[selectedSuggestionIndex]);
        } else {
          handleSearch();
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedSuggestionIndex(-1);
        break;
    }
  };

  const handleLocationKeyDown = (e: React.KeyboardEvent) => {
    if (!showLocationSuggestions || locationSuggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedLocationIndex(prev =>
          prev < locationSuggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedLocationIndex(prev =>
          prev > 0 ? prev - 1 : locationSuggestions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedLocationIndex >= 0 && selectedLocationIndex < locationSuggestions.length) {
          handleLocationSuggestionClick(locationSuggestions[selectedLocationIndex]);
        } else {
          handleSearch();
        }
        break;
      case 'Escape':
        setShowLocationSuggestions(false);
        setSelectedLocationIndex(-1);
        break;
    }
  };

  const handleSearch = (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }

    const params = new URLSearchParams();
    if (searchQuery.trim()) {
      params.set('q', searchQuery.trim());
    }

    if (locationQuery.trim()) {
      // For homepage, if they just type it, we use generic location
      params.set('location', locationQuery.trim());
    }

    navigate(`/search?${params.toString()}`);
  };

  const handleSuggestionClick = (suggestion: Suggestion) => {
    setSearchQuery(suggestion.title);
    setShowSuggestions(false);
    setSelectedSuggestionIndex(-1);

    const params = new URLSearchParams();
    params.set('q', suggestion.title);
    if (locationQuery.trim()) {
      params.set('location', locationQuery.trim());
    }
    navigate(`/search?${params.toString()}`);
  };

  const handleLocationSuggestionClick = (suggestion: LocationSuggestion) => {
    setLocationQuery(suggestion.full_name);
    setShowLocationSuggestions(false);
    setSelectedLocationIndex(-1);

    const params = new URLSearchParams();
    if (searchQuery.trim()) {
      params.set('q', searchQuery.trim());
    }

    if (suggestion.type === 'parish') {
      params.set('parish', suggestion.full_name);
    } else if (suggestion.type === 'city') {
      params.set('city', suggestion.full_name);
    } else {
      params.set('location', suggestion.full_name);
    }

    navigate(`/search?${params.toString()}`);
  };

  const handlePopularSearchClick = (searchTerm: string) => {
    const params = new URLSearchParams();
    params.append('q', searchTerm);
    navigate(`/search?${params.toString()}`);
  };

  // Function to render stars based on rating
const renderStars = (rating: number) => {
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;
  const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

  return (
    <div className="stars" style={{ display: 'flex', gap: '2px' }}>
      {[...Array(fullStars)].map((_, i) => (
        <span key={`full-${i}`} style={{ color: '#ffc107', fontSize: '16px' }}>★</span>
      ))}
      {hasHalfStar && <span style={{ color: '#ffc107', fontSize: '16px' }}>½</span>}
      {[...Array(emptyStars)].map((_, i) => (
        <span key={`empty-${i}`} style={{ color: '#e4e5e9', fontSize: '16px' }}>★</span>
      ))}
    </div>
  );
};

  return (
    <div className="min-h-screen bg-white">
      {/* Conditionally render header based on authentication */}
      {isAuthenticated ? <CustomerHeader /> : <Header />}

      {/* Hero Section */}
      <section className="premium-hero-sec">
        <div className="premium-hero-bg-shapes">
          <svg className="h-full w-full" viewBox="0 0 1440 600" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="200" cy="100" r="300" fill="white" />
            <circle cx="1200" cy="400" r="400" fill="white" />
          </svg>
        </div>
        <div className="premium-hero-content">
          <h1 className="premium-hero-title">Connect with Top Rated Professionals</h1>
          <p className="premium-hero-subtitle">We can connect you to the right Service, first time and every time.</p>
          
          <form onSubmit={handleSearch} className="premium-search-pill-card">
            {/* Search Input */}
            <div className="premium-search-field" ref={searchRef}>
              <Search size={20} />
              <input
                type="text"
                placeholder="Search for service"
                value={searchQuery}
                onChange={(e) => handleSearchInputChange(e.target.value)}
                onFocus={() => setShowSuggestions(true)}
                onKeyDown={handleKeyDown}
                autoComplete="off"
              />

              {/* Auto-suggestions dropdown */}
              {showSuggestions && (suggestions.length > 0 || isLoading || (searchQuery.length >= 2 && !isLoading && suggestions.length === 0)) && (
                <div className="search-suggestions" style={{ minWidth: '100%', width: '100%', left: 0, top: '105%' }}>
                  {isLoading && (
                    <div className="loading-container">
                      <div className="loading-spinner"></div>
                      <div className="loading-text">Searching...</div>
                    </div>
                  )}

                  {!isLoading && suggestions.length === 0 && searchQuery.length >= 2 && (
                    <div className="no-results-container">
                      <Search className="no-results-icon" />
                      <div className="no-results-title">No results found</div>
                      <div className="no-results-subtitle">Try searching for different keywords</div>
                    </div>
                  )}

                  {suggestions.map((suggestion, index) => (
                    <div
                      key={index}
                      onClick={() => handleSuggestionClick(suggestion)}
                      className={`suggestion-item ${index === selectedSuggestionIndex ? 'selected' : ''}`}
                    >
                      <div className="suggestion-icon">
                        {suggestion.type === 'category' && <Shield className="w-5 h-5" />}
                        {suggestion.type === 'skill' && <Star className="w-5 h-5" />}
                        {suggestion.type === 'location' && <Search className="w-5 h-5" />}
                        {suggestion.type === 'service' && <Wrench className="w-5 h-5" />}
                      </div>
                      <div className="suggestion-content">
                        <div className="suggestion-title">{suggestion.title}</div>
                        <div className="suggestion-description">{suggestion.description}</div>
                      </div>
                      <div className="suggestion-arrow">
                        <ArrowRight className="w-4 h-4" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Divider */}
            <div className="premium-search-divider"></div>

            {/* Location Input */}
            <div className="premium-search-field" ref={locationRef}>
              <MapPin size={20} />
              <input
                type="text"
                placeholder="Enter location (Parish, City)"
                value={locationQuery}
                onChange={(e) => handleLocationInputChange(e.target.value)}
                onFocus={() => setShowLocationSuggestions(true)}
                onKeyDown={handleLocationKeyDown}
                autoComplete="off"
              />

              {/* Location suggestions dropdown */}
              {showLocationSuggestions && (locationSuggestions.length > 0 || isLocationLoading || (locationQuery.length >= 2 && !isLocationLoading && locationSuggestions.length === 0)) && (
                <div className="search-suggestions" style={{ minWidth: '100%', width: '100%', left: 0, top: '105%' }}>
                  {isLocationLoading && (
                    <div className="loading-container">
                      <div className="loading-spinner"></div>
                      <div className="loading-text">Searching locations...</div>
                    </div>
                  )}

                  {!isLocationLoading && locationSuggestions.length === 0 && locationQuery.length >= 2 && (
                    <div className="no-results-container">
                      <Search className="no-results-icon" />
                      <div className="no-results-title">No locations found</div>
                      <div className="no-results-subtitle">Try searching for a different parish or city</div>
                    </div>
                  )}

                  {locationSuggestions.map((suggestion, index) => (
                    <div
                      key={index}
                      onClick={() => handleLocationSuggestionClick(suggestion)}
                      className={`suggestion-item ${index === selectedLocationIndex ? 'selected' : ''}`}
                    >
                      <div className="suggestion-icon">
                        {suggestion.type === 'parish' && <Shield className="w-5 h-5" />}
                        {suggestion.type === 'city' && <Search className="w-5 h-5" />}
                        {suggestion.type === 'zone' && <Star className="w-5 h-5" />}
                      </div>
                      <div className="suggestion-content">
                        <div className="suggestion-title">{suggestion.full_name}</div>
                        <div className="suggestion-description">
                          {suggestion.type === 'parish' ? 'Parish' : suggestion.type === 'city' ? 'City' : 'Zone'} in Jamaica
                        </div>
                      </div>
                      <div className="suggestion-arrow">
                        <ArrowRight className="w-4 h-4" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button type="submit" className="premium-search-btn">
              <Search size={16} />
              <span>Search</span>
            </button>
          </form>
        </div>
      </section>

      {/* Skilled Worker Promo Bar */}
      <section className="premium-promo-bar">
        <div className="premium-promo-inner">
          <div className="premium-promo-left">
            <div className="premium-promo-icon-box">
              <Wrench size={24} />
            </div>
            <div>
              <h3 className="premium-promo-title">Are you a skilled worker?</h3>
              <p className="premium-promo-sub">Join our community of professionals and start getting hired today.</p>
            </div>
          </div>
          <Link to="/talent/register" className="premium-promo-btn">
            <span>Register now</span>
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* Categories Section */}
      <section className="premium-categories-sec">
        <h2 className="premium-section-title">Explore our <span>Categories</span></h2>
        <p className="premium-section-subtitle">
          Service categories help organize and structure the offerings on a marketplace, making it easier for users to find what they need.
        </p>
        
        <div className="premium-categories-grid">
          {popularData?.popularCategories && popularData.popularCategories.length > 0 ? (
            popularData.popularCategories.map((category) => (
              <div 
                key={category.id} 
                className="premium-cat-card"
                onClick={() => handlePopularSearchClick(category.name)}
              >
                <div className="premium-cat-icon-container">
                  {getCategoryLucideIcon(category.name)}
                </div>
                <span>{category.name}</span>
              </div>
            ))
          ) : (
            // Fallback content matching the 12 categories in target screenshot
            [
              { name: 'Moving & Storage' },
              { name: 'Pet Services' },
              { name: 'Automotive' },
              { name: 'Financial Services' },
              { name: 'Event Planning' },
              { name: 'Beauty & Wellness' },
              { name: 'Home Services' },
              { name: 'Technology' },
              { name: 'Cleaning' },
              { name: 'Photography' },
              { name: 'Catering' },
              { name: 'Tutoring' }
            ].map((cat, i) => (
              <div 
                key={i} 
                className="premium-cat-card"
                onClick={() => handlePopularSearchClick(cat.name)}
              >
                <div className="premium-cat-icon-container">
                  {getCategoryLucideIcon(cat.name)}
                </div>
                <span>{cat.name}</span>
              </div>
            ))
          )}
        </div>

        <div className="category-btn mt-4">
          <Link to="/categories" className="premium-view-all-link">
            <span>View all</span>
            <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      {/* How SkillSeek Works Section */}
      <section className="premium-works-sec">
        <h2 className="premium-section-title">How SkillSeek Works</h2>
        <p className="premium-section-subtitle">Finding skilled professionals has never been easier</p>
        
        <div className="premium-works-grid">
          <div className="premium-work-step">
            <div className="premium-work-icon-box">
              <Search size={24} />
            </div>
            <div className="premium-work-badge">STEP 1</div>
            <h4>Search for skills</h4>
            <p>Easily find workers based on their skills, location, and reviews.</p>
            <div className="premium-work-connector"></div>
          </div>
          
          <div className="premium-work-step">
            <div className="premium-work-icon-box">
              <User size={24} />
            </div>
            <div className="premium-work-badge">STEP 2</div>
            <h4>View profiles</h4>
            <p>Browse detailed profiles with portfolios, experience, and certifications.</p>
            <div className="premium-work-connector"></div>
          </div>
          
          <div className="premium-work-step">
            <div className="premium-work-icon-box">
              <ArrowRight size={24} />
            </div>
            <div className="premium-work-badge">STEP 3</div>
            <h4>Connect directly</h4>
            <p>Contact workers directly to discuss your project requirements.</p>
            <div className="premium-work-connector"></div>
          </div>
          
          <div className="premium-work-step">
            <div className="premium-work-icon-box">
              <Star size={24} />
            </div>
            <div className="premium-work-badge">STEP 4</div>
            <h4>Leave reviews</h4>
            <p>Share your experience and help others find quality workers.</p>
          </div>
        </div>
      </section>

      {/* Recent Services Section */}
      <RecentServices limit={3} showViewAll={true} />

      {/* Testimonials Section */}
      <section className="premium-reviews-sec" style={{ overflow: 'hidden' }}>
        <h2 className="premium-section-title">Genuine <span>reviews</span> from Customers</h2>
        <p className="premium-section-subtitle">Each listing is designed to be clear and concise, providing customers with peace of mind.</p>
        
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 12px' }}>
          <Slider {...settings}>
            {reviews && reviews.length > 0 ? (
              reviews.map((review) => (
                <div key={review.id}>
                  <div className="premium-review-card" style={{ minHeight: '300px' }}>
                    <div className="premium-review-stars">
                      {renderStars(review.rating)}
                    </div>
                    <h4 className="premium-review-title">Quality of work was excellent</h4>
                    <p className="premium-review-text">
                      "{review.reviewText ? (review.reviewText.split(' ').length > 40 ? review.reviewText.split(' ').slice(0, 40).join(' ') + '...' : review.reviewText) : ''}"
                    </p>
                    <div className="premium-review-author">
                      <div className="premium-review-avatar">
                        {review.reviewerPicture ? (
                          <img src={review.reviewerPicture} alt={review.reviewerName} onError={(e) => { (e.target as HTMLImageElement).src = ''; }} />
                        ) : (
                          review.reviewerName ? review.reviewerName.charAt(0) : 'C'
                        )}
                      </div>
                      <div>
                        <h5 className="premium-review-author-name">{review.reviewerName || 'Customer'}</h5>
                        <p className="premium-review-author-role">{review.serviceTitle || 'Customer'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              // Fallback reviews matching screenshot exactly
              [
                {
                  id: 1,
                  rating: 5,
                  title: 'Quality of work was excellent',
                  text: 'The quality of work was excellent. The carpenter arrived on time, was incredibly professional, and the finished shelves look amazing. Would definitely recommend!',
                  name: 'Sarah Mitchell',
                  role: 'Homeowner'
                },
                {
                  id: 2,
                  rating: 5,
                  title: 'Quality of work was excellent',
                  text: 'Found a fantastic photographer for our corporate event through SkillSeek. The entire process from search to booking was seamless. Five stars!',
                  name: 'James Rodriguez',
                  role: 'Business Owner'
                },
                {
                  id: 3,
                  rating: 5,
                  title: 'Quality of work was excellent',
                  text: "We use SkillSeek regularly for maintenance work across our properties. Every professional we've hired has been top-notch. It saves us so much time.",
                  name: 'Aisha Patel',
                  role: 'Property Manager'
                }
              ].map((rev) => (
                <div key={rev.id}>
                  <div className="premium-review-card" style={{ minHeight: '300px' }}>
                    <div className="premium-review-stars">
                      <Star size={16} fill="#EF9F27" stroke="#EF9F27" />
                      <Star size={16} fill="#EF9F27" stroke="#EF9F27" />
                      <Star size={16} fill="#EF9F27" stroke="#EF9F27" />
                      <Star size={16} fill="#EF9F27" stroke="#EF9F27" />
                      <Star size={16} fill="#EF9F27" stroke="#EF9F27" />
                    </div>
                    <h4 className="premium-review-title">{rev.title}</h4>
                    <p className="premium-review-text">"{rev.text}"</p>
                    <div className="premium-review-author">
                      <div className="premium-review-avatar">
                        <User size={18} />
                      </div>
                      <div>
                        <h5 className="premium-review-author-name">{rev.name}</h5>
                        <p className="premium-review-author-role">{rev.role}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </Slider>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage; 
